import { Component, Input } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { NgxCurrencyDirective } from 'ngx-currency';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { LookupItemStruct } from '../../../../../shared/services/structs/pessoa/lookup-item.struct';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ProdutoService } from '../../../../../shared/services/apis/produto/produto.service';
import {
  ProdutoVariacaoValorStruct,
  VariacaoValorStruct,
} from '../../../../../shared/services/structs/produto/produto-variacao-valor.struct';
import { CombinacaoVariacaoRequest } from '../../../../../shared/services/requests/produto/combinacao-variacao.request';
import { ProdutoVariacaoItemStruct } from '../../../../../shared/services/structs/produto/produto-variacao-item.struct';
import { LookupService } from '../../../../../shared/services/apis/produto/produto-lookup.service';
import { MatDialog } from '@angular/material/dialog';
import { VariacaoEstoqueModalComponent } from './variacao-estoque-modal/variacao-estoque-modal.component';
import { ConfiguracaoProduto } from '../../../../../shared/services/models/produto/configuracao-produto.model';
import { TipoGeracaoSkuEnum } from '../../../../../shared/enums/produto/tipo-geracao-sku.enum';
import { MatBadgeModule } from '@angular/material/badge';
import { LocalizacaoEstoque } from '../../../../../shared/services/models/produto/localizacao-estoque.model';
import { GetVariacoesGeradasResponse } from '../../../../../shared/services/responses/produto/get-variacoes-geradas.response';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';

@Component({
  selector: 'app-produto-variacoes',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatTooltipModule,
    MatAutocompleteModule,
    NgxCurrencyDirective,
    MatBadgeModule,
    SelectPesquisavelComponent,
    EmptyStateComponent,
  ],
  templateUrl: './produto-variacoes.component.html',
  styleUrl: './produto-variacoes.component.css',
})
export class ProdutoVariacoesComponent {
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) isVariacao!: boolean;
  @Input() isEditMode: boolean = false;
  @Input() configuracaoProduto: ConfiguracaoProduto | null = null;
  @Input() listaLocalizacaoEstoque: LocalizacaoEstoque[] = [];
  @Input() listaVariacao: LookupItemStruct[] = [];
  @Input() listaPreco: any[] = [];
  @Input() quantidadeMask: any;
  @Input() isFracionada = true;
  @Input() set dadosVariacoesProdutoCarregados(dados: ProdutoVariacaoItemStruct[]) {
    if (dados) this.preencherCombinacoes(dados);
  }
  @Input() set dadosVariacoesCarregados(dados: ProdutoVariacaoValorStruct[]) {
    if (dados) this.preencherVariacoes(dados);
  }
  private allVariacaoValores: LookupItemStruct[] = []; // Cache de todos valores de variação
  public variationValuesMap: Map<number, LookupItemStruct[]> = new Map();
  public variationValuesMapOriginal: Map<number, LookupItemStruct[]> = new Map();
  isLoading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private produtoService: ProdutoService,
    private alertService: AlertService,
    private lookupService: LookupService,
    private dialog: MatDialog
  ) {}

  get variacoes(): FormArray {
    return this.form.get('variacoes') as FormArray;
  }

  get combinacoes(): FormArray {
    return this.form.get('combinacoes') as FormArray;
  }

  private preencherCombinacoes(dados: any[]): void {
    this.combinacoes.clear();
    dados.forEach(item => this.addCombinacaoRow(item));
  }

  private preencherVariacoes(dados: any[]): void {
    this.variacoes.clear();
    dados.forEach(item => this.addVariacaoRow(item));
  }

  public isSkuDisabled(): boolean {
    if (!this.configuracaoProduto) return false;
    const tipoGeracao = this.configuracaoProduto.idTipoGeracaoSku;
    return (
      tipoGeracao === TipoGeracaoSkuEnum.Sequencial || tipoGeracao === TipoGeracaoSkuEnum.SequencialEAN13
    );
  }

  getControl(item: any, controlName: string): FormControl {
    return item.get(controlName) as FormControl;
  }

  addCombinacaoRow(data?: ProdutoVariacaoItemStruct): void {
    console.log(this.isSkuDisabled());
    const group = this.fb.group({
      idProduto: [data?.idProduto],
      nomeVariavel: [data?.nomeVariavel], // e.g. "Camiseta - Azul - P"
      codigoSku: [
        {
          value: data?.codigoSku || '',
          disabled: this.isSkuDisabled(),
        },
      ],
      gtinEan: [data?.gtinEan],
      // can store other fields if needed, like price/stock override
      estoque: this.fb.array(this.mapearEstoqueInicial(data?.estoque)),
      preco: [data?.precos?.[0]?.precoVenda || 0], // Simplified for now
      listProdutoVariacaoValor: [data?.listProdutoVariacaoValor || []],
    });
    this.combinacoes.push(group);
  }
  removeCombinacaoRow(index: number): void {
    this.combinacoes.removeAt(index);
  }

  addVariacaoRow(data?: any): void {
    if (this.variacoes.length >= 5) {
      this.alertService.show('Atenção', 'Máximo de 5 variações permitidas.', AlertType.warning);
      return;
    }

    // Validate if previous row has a selected variation
    if (this.variacoes.length > 0) {
      const lastRow = this.variacoes.at(this.variacoes.length - 1);
      if (!lastRow.get('idVariacao')?.value) {
        this.alertService.show(
          'Atenção',
          'Selecione o tipo de variação na linha anterior antes de adicionar uma nova.',
          AlertType.warning
        );
        return;
      }
    }
    const group = this.fb.group({
      idVariacao: [data?.idVariacao, Validators.required],
      listVariacaoValor: [data?.listVariacaoValor || [], Validators.required], // Array de valores selecionados
      nomeVariacao: [data?.nomeVariacao || ''],
    });

    const index = this.variacoes.length;
    group.get('idVariacao')?.valueChanges.subscribe(val => {
      this.loadVariationValues(index, val);
      group.get('listVariacaoValor')?.setValue([]);
    });

    this.variacoes.push(group);

    if (data?.idVariacao) {
      this.loadVariationValues(index, data.idVariacao);
    }
  }
  removeVariacaoRow(index: number): void {
    this.variacoes.removeAt(index);
  }

  /**
   * Inicializa o estoque de uma combinação já com a localização padrão — espelhando o que
   * o modal "Estoque por Local" faz ao ser aberto (checkDefaultStockLocation) — para que a
   * localização exista mesmo que o usuário nunca abra o modal para essa combinação (o que
   * antes deixava a variação sem nenhum EstoqueSaldoProduto com localização ao salvar).
   */
  private mapearEstoqueInicial(estoque: any[] = []): FormGroup[] {
    if (!estoque.length) {
      const localizacaoPadrao = this.listaLocalizacaoEstoque?.find(l => l.isPadrao);
      if (localizacaoPadrao) {
        estoque = [{
          idLocalizacaoEstoque: localizacaoPadrao.idLocalizacaoEstoque,
          saldoFisico: 0,
          saldoDisponivel: 0,
          estoqueMinimo: 0,
          estoqueMaximo: 0,
        }];
      }
    }
    return estoque.map(e =>
      this.fb.group({
        idLocalizacaoEstoque: [e.idLocalizacaoEstoque, Validators.required],
        saldoFisico: [e.saldoFisico ?? null],
        saldoDisponivel: [e.saldoDisponivel ?? null],
        estoqueMinimo: [e.estoqueMinimo ?? null],
        estoqueMaximo: [e.estoqueMaximo ?? null],
        idEstoqueSaldoProduto: [e.idEstoqueSaldoProduto || null],
        lotes: [e.lotes || []],
        series: [e.series || []],
      })
    );
  }

  /** Tipo de rastreio ativo no produto (lote OU série; exclusivos). */
  public get tipoRastreio(): 'lote' | 'serie' | null {
    if (this.form?.get('isControlaLotes')?.value === true) return 'lote';
    if (this.form?.get('isControlaSeries')?.value === true) return 'serie';
    return null;
  }

  /** True quando a variação tem saldo mas falta lote/série (para o selo "Estoque incompleto"). */
  public variacaoIncompleta(combinacao: any): boolean {
    const tipo = this.tipoRastreio;
    if (!tipo || this.isEditMode) return false;
    const estoque = combinacao.get('estoque') as FormArray;
    return estoque.controls.some(loc => {
      const saldo = Number(loc.get('saldoFisico')?.value) || 0;
      if (saldo <= 0) return false;
      if (tipo === 'lote') {
        const lotes: any[] = loc.get('lotes')?.value ?? [];
        const soma = lotes.reduce((s, l) => s + (Number(l.quantidade) || 0), 0);
        return !(lotes.length > 0 && Math.abs(soma - saldo) < 0.0001 && lotes.every(l => (l.codigoLote || '').trim()));
      }
      const series: any[] = loc.get('series')?.value ?? [];
      return !(series.length === Math.round(saldo) && series.every(s => (s.codigoSerie || '').trim()));
    });
  }

  adicionarVariacaoValor(rowIndex: number, event: any, input: HTMLInputElement): void {
    const value = event.option.value;
    const control = this.variacoes.at(rowIndex).get('listVariacaoValor') as FormControl;
    const currentValues = control.value || [];

    // Evitar duplicatas
    const isDuplicate = currentValues.some(
      (v: any) =>
        (typeof v === 'object' && v.id === value.id) || (typeof v === 'string' && v === value.descricao)
    );

    if (!isDuplicate) {
      control.setValue([...currentValues, value]);
    }

    input.value = '';
    this.filterVariationValues(rowIndex, '');
  }

  removeVariacaoValor(rowIndex: number, chipIndex: number): void {
    const control = this.variacoes.at(rowIndex).get('listVariacaoValor') as FormControl;
    const currentValues = control.value || [];
    currentValues.splice(chipIndex, 1);
    control.setValue([...currentValues]);
  }

  private loadVariationValues(index: number, idVariacao: number): void {
    if (!idVariacao) {
      this.variationValuesMap.set(index, []);
      this.variationValuesMapOriginal.set(index, []);
      return;
    }

    // Se já temos o cache, apenas filtrar
    if (this.allVariacaoValores.length > 0) {
      const filtered = this.allVariacaoValores.filter(
        (v: LookupItemStruct) => v.codigo === idVariacao.toString()
      );
      this.variationValuesMap.set(index, filtered);
      this.variationValuesMapOriginal.set(index, filtered);
    } else {
      // Primeira vez: carregar todos os valores de uma vez
      this.lookupService.getAllSelectVariacaoValor(idVariacao).subscribe({
        next: res => {
          if (!res.isError && res.list) {
            // Armazenar no cache
            this.allVariacaoValores = res.list;
            // Filtrar para a variação selecionada baseado no codigo
            const filtered = res.list.filter((v: LookupItemStruct) => v.codigo === idVariacao.toString());
            this.variationValuesMap.set(index, filtered);
            this.variationValuesMapOriginal.set(index, filtered);
          }
        },
      });
    }
  }

  gerarGrade(): void {
    if (this.variacoes.invalid) {
      this.alertService.show(
        'Atenção',
        'Selecione as variações e seus valores corretamente.',
        AlertType.warning
      );
      return;
    }

    const request = new CombinacaoVariacaoRequest();
    request.codigoSkuPai = this.form.get('codigoSku')?.value;
    request.listVariacoes = this.variacoes.value.map((v: any) => {
      const variacaoStruct = new ProdutoVariacaoValorStruct();
      variacaoStruct.idVariacao = v.idVariacao;

      // listVariacaoValor pode conter objetos selecionados ou strings digitadas
      variacaoStruct.listVariacaoValor = (v.listVariacaoValor || []).map((valor: any) => {
        const valorStruct = new VariacaoValorStruct();

        // Se for um objeto da lista (tem id), pegar o id
        if (typeof valor === 'object' && valor.id) {
          valorStruct.idVariacaoValor = valor.id;
          valorStruct.valor = valor.descricao || '';
        }
        // Se for string digitada (novo valor), enviar como null no id
        else if (typeof valor === 'string') {
          valorStruct.idVariacaoValor = null;
          valorStruct.valor = valor;
        }
        // Fallback se já vier no formato correto
        else {
          valorStruct.idVariacaoValor = valor.idVariacaoValor || null;
          valorStruct.valor = valor.valor || '';
        }

        return valorStruct;
      });

      return variacaoStruct;
    });

    // "Gerar variação" refaz a grade do zero: o backend sempre devolve idProduto=null para
    // toda combinação (mesmo as que já existem). Sem isto, cada clique recriava as combinações
    // já salvas em vez de atualizá-las — o backend passava a tratá-las como novas duplicadas.
    // Guardamos as combinações atuais por assinatura (ids de variação-valor) para reaproveitar
    // idProduto/SKU/EAN/preço/estoque reais quando a combinação já existia.
    const existentesPorAssinatura = new Map<string, any>();
    this.combinacoes.getRawValue().forEach((c: any) => {
      const assinatura = this.assinaturaCombinacao(c.listProdutoVariacaoValor);
      if (assinatura) existentesPorAssinatura.set(assinatura, c);
    });

    const defaultList = this.listaPreco.find(l => l.listaPreco?.isPadrao);

    this.isLoading = true;
    this.produtoService.gerarGrade(request).subscribe({
      next: res => {
        console.log(res);
        this.isLoading = false;
        if (!res.isError) {
          this.updateVariationValuesFromResponse(res);
          this.combinacoes.clear();

          let defaultPrice = 0;
          // Se não for edição, pega o preço da tabela padrão na lista de preços do produto
          if (!this.isEditMode) {
            const precosArray = this.form.get('precos') as FormArray;

            if (defaultList && precosArray) {
              const priceCtrl = precosArray.controls.find(
                c => c.get('idListaPreco')?.value === defaultList.id
              );
              defaultPrice = priceCtrl?.get('precoVenda')?.value || 0;
            }
          }

          res.listVariacaoItemStruct.forEach(item => {
            const assinatura = this.assinaturaCombinacao(item.listProdutoVariacaoValor as any[]);
            const existente = assinatura ? existentesPorAssinatura.get(assinatura) : null;

            if (existente) {
              // Combinação já existia: preserva o vínculo persistido em vez de tratar como nova.
              item.idProduto = existente.idProduto;
              item.codigoSku = existente.codigoSku || item.codigoSku;
              item.gtinEan = existente.gtinEan || item.gtinEan;
              if (existente.estoque?.length) item.estoque = existente.estoque;
              if (existente.preco && defaultList) {
                item.precos = [{ idListaPreco: defaultList.id, precoVenda: existente.preco } as any];
              }
            } else if (!this.isEditMode && defaultPrice > 0 && defaultList) {
              // Se for criação (!isEditMode) e tivermos um preço padrão encontrado, override.
              // Caso contrário, usa o que veio do backend (que deve ser 0 ou default)
              if (!item.precos) item.precos = [];
              if (item.precos.length > 0) {
                item.precos[0].precoVenda = defaultPrice;
              } else {
                item.precos.push({ idListaPreco: defaultList.id, precoVenda: defaultPrice } as any);
              }
            }
            this.addCombinacaoRow(item);
          });
        } else {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
        }
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao gerar grade de variações.', AlertType.error);
      },
    });
  }

  /** Assinatura de uma combinação a partir dos ids de variação-valor (ordenados), para casar
   * uma combinação já existente com o item equivalente devolvido por uma nova "gerar grade". */
  private assinaturaCombinacao(listProdutoVariacaoValor: any[] | undefined): string | null {
    const ids = (listProdutoVariacaoValor || [])
      .flatMap((pv: any) => (pv.listVariacaoValor || []).map((v: any) => v.idVariacaoValor))
      .filter((id: any) => id != null)
      .sort((a: number, b: number) => a - b);
    return ids.length ? ids.join('-') : null;
  }

  // Filter the autocomplete options
  filterVariationValues(index: number, text: string): void {
    const original = this.variationValuesMapOriginal.get(index) || [];
    if (!text) {
      this.variationValuesMap.set(index, original);
      return;
    }
    const filterValue = text.toLowerCase();
    const filtered = original.filter(option => option.descricao.toLowerCase().includes(filterValue));
    this.variationValuesMap.set(index, filtered);
  }

  // Handle adding a custom value via 'Enter' (MatChipInputTokenEnd)
  addCustomVariationValue(index: number, event: any): void {
    const value = (event.value || '').trim();
    const input = event.input; // or event.chipInput.inputElement

    if (value) {
      const control = this.variacoes.at(index).get('listVariacaoValor') as FormControl;
      const currentValues = control.value || [];

      // Check duplicate
      const isDuplicate = currentValues.some(
        (v: any) =>
          (typeof v === 'object' && v.descricao === value) || // Existing object
          (typeof v === 'string' && v === value) // Previously added string
      );

      if (!isDuplicate) {
        // If it exists in the original list, use the object instead of string
        const originalList = this.variationValuesMapOriginal.get(index) || [];
        const existingOption = originalList.find(o => o.descricao.toLowerCase() === value.toLowerCase());

        if (existingOption) {
          control.setValue([...currentValues, existingOption]);
        } else {
          // Add as string (custom value)
          control.setValue([...currentValues, value]);
        }
      }
    }

    // Reset input
    if (event.chipInput) {
      event.chipInput.clear();
    } else {
      // Fallback if needed, though MatChipInputEvent has chipInput
      if (input) input.value = '';
    }

    // Reset filter
    this.filterVariationValues(index, '');
  }

  public getListaVariacoesDisponiveis(currentIndex: number): any[] {
    const idsSelecionados = this.variacoes.controls
      .map((ctrl, index) => (index !== currentIndex ? ctrl.get('idVariacao')?.value : null))
      .filter(id => id !== null);

    return this.listaVariacao.map(v => ({
      ...v,
      disabled: idsSelecionados.includes(Number(v.id)),
    }));
  }

  abrirModalEstoque(index: number): void {
    const item = this.combinacoes.at(index);
    const estoqueArray = item.get('estoque') as FormArray;

    const dialogRef = this.dialog.open(VariacaoEstoqueModalComponent, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        nomeVariavel: item.get('nomeVariavel')?.value,
        estoqueAtual: estoqueArray.getRawValue(),
        listaLocalizacaoEstoque: this.listaLocalizacaoEstoque,
        tipoRastreio: this.tipoRastreio,
        isEditMode: this.isEditMode,
        quantidadeMask: this.quantidadeMask,
        isFracionada: this.isFracionada,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        estoqueArray.clear();
        result.forEach((res: any) => {
          estoqueArray.push(
            this.fb.group({
              idLocalizacaoEstoque: [res.idLocalizacaoEstoque, Validators.required],
              saldoFisico: [res.saldoFisico],
              saldoDisponivel: [res.saldoDisponivel],
              estoqueMinimo: [res.estoqueMinimo],
              estoqueMaximo: [res.estoqueMaximo],
              idEstoqueSaldoProduto: [res.idEstoqueSaldoProduto],
              lotes: [res.lotes || []],
              series: [res.series || []],
            })
          );
        });
        item.markAsDirty();
      }
    });
  }
  private updateVariationValuesFromResponse(response: GetVariacoesGeradasResponse): void {
    const tempMap = new Map<number, Map<string, number>>();

    // 1. Build map of all values returned in the grid (ID Variacao -> Value String -> ID Value)
    if (response.listVariacaoItemStruct) {
      response.listVariacaoItemStruct.forEach(item => {
        if (item.listProdutoVariacaoValor) {
          item.listProdutoVariacaoValor.forEach(pVal => {
            if (!tempMap.has(pVal.idVariacao)) {
              tempMap.set(pVal.idVariacao, new Map<string, number>());
            }
            const valueMap = tempMap.get(pVal.idVariacao);
            if (pVal.listVariacaoValor) {
              pVal.listVariacaoValor.forEach(vVal => {
                if (vVal.idVariacaoValor && vVal.valor) {
                  valueMap?.set(vVal.valor.toLowerCase(), vVal.idVariacaoValor);
                }
              });
            }
          });
        }
      });
    }

    // 2. Update form controls and original maps
    this.variacoes.controls.forEach((control, index) => {
      const idVariacao = control.get('idVariacao')?.value;
      if (!idVariacao) return;

      const valueMap = tempMap.get(idVariacao);
      if (!valueMap) return;

      const listControl = control.get('listVariacaoValor');
      const currentValues = listControl?.value || [];
      const newValues: any[] = [];
      let hasChanges = false;

      currentValues.forEach((val: any) => {
        // If it's a string (custom value), try to find its ID in the response map
        if (typeof val === 'string') {
          const foundId = valueMap.get(val.toLowerCase()) || valueMap.get(val); // Try both
          if (foundId) {
            const newObj = {
              id: foundId,
              descricao: val,
              codigo: idVariacao.toString(), // Assuming code matches idVariacao for filtering
            };
            newValues.push(newObj);
            hasChanges = true;

            // Add to original map if missing
            const originalList = this.variationValuesMapOriginal.get(index) || [];
            if (!originalList.find(o => o.id === foundId)) {
              originalList.push(newObj as LookupItemStruct);
              this.variationValuesMapOriginal.set(index, originalList);
              // Also update current filtered map if it's showing all
              this.variationValuesMap.set(index, originalList);
            }
          } else {
            // Keep as string if not found (shouldn't happen if backend worked)
            newValues.push(val);
          }
        } else {
          // Already an object, keep it
          newValues.push(val);
        }
      });

      if (hasChanges) {
        listControl?.setValue(newValues);
      }
    });
  }
}
